import React, { useState, forwardRef } from 'react';
import emailjs from 'emailjs-com';

interface FormData extends Record<string, unknown> {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const Contact: React.ForwardRefRenderFunction<HTMLElement> = (props, ref) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Message should be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    debugger;

    if (validateForm()) {
      setIsSubmitting(true);

      const serviceID = 'service_l8gfra3';
      const templateID = 'template_a7emxvg';
      const userID = 'P3C4_Zdw_u1WRNlUX';

      // Send email using EmailJS
      emailjs.send(
        serviceID,
        templateID,
        formData,
        userID
      )
      .then(
        () => {
          setIsSubmitting(false);
          setSubmitStatus('success');
          setFormData({ name: '', email: '', subject: '', message: '' });

          // Reset success message after 5 seconds
          setTimeout(() => {
            setSubmitStatus(null);
          }, 5000);
        },
        (error) => {
          setIsSubmitting(false);
          setSubmitStatus('error');
        }
      )
    }
  };

  return (
    <section id="contact" className="section-padding" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 section-reveal" style={{ animationDelay: '0.2s' }}>
          <h2 className="text-white md:text-4xl font-bold section-title text-center">
            Contact Me
          </h2>
          <p className="text-white mt-4 max-w-3xl mx-auto">
            Have a project in mind or want to discuss how I can help your business? 
            Fill out the form below, and I'll get back to you as soon as possible.
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
            <div className="md:col-span-2 section-reveal" style={{ animationDelay: '0.4s' }}>
              <div className="bg-white rounded-lg shadow-md p-6"style={{height: '60%'}}>
                <h3 className="text-xl font-bold mb-4">Contact Information</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start section-reveal" style={{ animationDelay: '00.6s' }}>
                    <div className="bg-blue-100 text-primary p-3 rounded-lg mr-4">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-700">Email</h4>
                      <a href="mailto:iamzee4@gmail.com" className="text-primary hover:underline">
                        iamzee4@gmail.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start section-reveal" style={{ animationDelay: '0.8s' }}>
                    <div className="bg-blue-100 text-primary p-3 rounded-lg mr-4">
                      <i className="fas fa-phone"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-700">Phone</h4>
                      <a href="tel:+923361055952" className="text-gray-600">
                        +92 (336) 105-5952
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start section-reveal" style={{ animationDelay: '1.0s' }}>
                    <div className="bg-blue-100 text-primary p-3 rounded-lg mr-4">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-700">Location</h4>
                      <p className="text-gray-600">
                        Islamabad, Pakistan
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="md:col-span-3 section-reveal" style={{ animationDelay: '0.6s' }}>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Send Me a Message</h3>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 bg-green-100 text-green-700 p-4 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-check-circle mr-2"></i>
                      <span>Your message has been sent successfully! I'll get back to you soon.</span>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-gray-700 mb-2">Your Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`form-input w-full px-4 py-2 border rounded-lg focus:outline-none ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="John Doe"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-gray-700 mb-2">Your Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`form-input w-full px-4 py-2 border rounded-lg focus:outline-none ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="johndoe@example.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="subject" className="block text-gray-700 mb-2">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`form-input w-full px-4 py-2 border rounded-lg focus:outline-none ${errors.subject ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Project Inquiry"
                    />
                    {errors.subject && (
                      <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
                    )}
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-gray-700 mb-2">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className={`form-input w-full px-4 py-2 border rounded-lg focus:outline-none ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Hello, I'd like to discuss a project..."
                    >
                    </textarea>
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    className="btn-primary py-3 px-8 rounded-lg text-white font-semibold inline-flex items-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>Send Message</>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default forwardRef(Contact);